import request from "../../utils/request";
import { Result } from "../../models/general";

export enum BusinessRoleType {
  System = "System",
  Project = "Project",
  Temporary = "Temporary",
}

export enum BusinessRoleStatus {
  Active = "Active",
  Inactive = "Inactive",
  Draft = "Draft",
}

export interface BusinessRoleDependencyView {
  role: string;
  description: string;
}

export interface BusinessRoleSuccessCriteriaView {
  description?: string;
  lastReviewDate?: string;
  nextReviewDate?: string;
  reviewedBy?: string;
}

export interface BusinessRoleView {
  id: number;
  name: string;
  type: BusinessRoleType;
  description?: string;
  category?: string;
  status: BusinessRoleStatus;
  numberOfSeats: number;
  assignedCount: number;
  coreResponsibilities: string[];
  decisionRights: string[];
  dependencies: BusinessRoleDependencyView[];
  successCriteria?: BusinessRoleSuccessCriteriaView;
  tags: string[];
  requiredSkills: string[];
  neededEquipment?: string[];
  createdOn: string;
  updatedOn?: string;
}

export interface BusinessRoleListView {
  items: BusinessRoleView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface BusinessRoleStatsView {
  total: number;
  active: number;
  inactive: number;
  draft: number;
  unassigned: number;
  dueToRevision: number;
  needsClarity: number;
}

export interface BusinessRoleRevisionView {
  id: number;
  changedById: string;
  changedByName?: string;
  changedAt: string;
  fieldName: string;
  oldValue?: string;
  newValue?: string;
  comment?: string;
}

export interface BusinessRoleDependencyInput {
  role: string;
  description: string;
}

export interface BusinessRoleSuccessCriteriaInput {
  description?: string;
  lastReviewDate?: string;
  nextReviewDate?: string;
  reviewedBy?: string;
}

export interface CreateBusinessRoleInput {
  name: string;
  type: BusinessRoleType;
  description?: string;
  category?: string;
  status: BusinessRoleStatus;
  numberOfSeats: number;
  coreResponsibilities?: string[];
  decisionRights?: string[];
  dependencies?: BusinessRoleDependencyInput[];
  successCriteria?: BusinessRoleSuccessCriteriaInput;
  tags?: string[];
  requiredSkills?: string[];
  neededEquipment?: string[];
}

export interface UpdateBusinessRoleInput extends CreateBusinessRoleInput {
  id: number;
  revisionComment?: string;
}

export interface BusinessRoleListQuery {
  search?: string;
  status?: BusinessRoleStatus;
  type?: BusinessRoleType;
  category?: string;
  filterTab?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

// Role Assignment types (Story 4.2)

export enum RoleAssignmentStatus {
  Active = "Active",
  Expired = "Expired",
  Revoked = "Revoked",
}

export interface RoleAssignmentView {
  id: number;
  businessRoleId: number;
  roleName: string;
  roleType: BusinessRoleType;
  employeeId: string;
  employeeName?: string;
  effectiveDate: string;
  expiryDate?: string;
  notes?: string;
  status: RoleAssignmentStatus;
  assignedByName?: string;
  assignedAt: string;
}

export interface RoleConflictView {
  type: string;
  severity: string;
  message: string;
  existingRoleId?: number;
  existingRoleName?: string;
  overlappingResponsibilities: string[];
}

export interface ConflictDetectionResult {
  hasConflicts: boolean;
  conflicts: RoleConflictView[];
}

export interface RoleAssignmentListView {
  items: RoleAssignmentView[];
  totalCount: number;
}

export interface AssignRoleInput {
  businessRoleId: number;
  employeeId: string;
  effectiveDate: string;
  expiryDate?: string;
  notes?: string;
  forceAssign: boolean;
}

export interface UnassignRoleInput {
  assignmentId: number;
  notes?: string;
}

export interface EmployeeSimpleView {
  id: string;
  fullName: string;
  department?: string;
}

// Bulk Assignment types (Story 4.3)

export interface BulkAssignRoleInput {
  businessRoleId: number;
  employeeIds: string[];
  effectiveDate: string;
  expiryDate?: string;
  notes?: string;
  forceAssign: boolean;
}

export interface BulkAssignmentEmployeeResult {
  employeeId: string;
  employeeName?: string;
  success: boolean;
  errorMessage?: string;
  conflicts: RoleConflictView[];
}

export interface BulkAssignmentResult {
  totalRequested: number;
  successCount: number;
  failedCount: number;
  results: BulkAssignmentEmployeeResult[];
}

export interface UnifiedRevisionView {
  id: number;
  eventType: string; // "definition_change" | "assignment" | "revocation"
  changedById: string;
  changedByName?: string;
  changedAt: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  comment?: string;
  employeeId?: string;
  employeeName?: string;
}

// Role-Skill Alignment types (Task 12)

export interface RoleSkillAlignmentView {
  businessRoleId: number;
  roleName: string;
  matchedCount: number;
  missingCount: number;
  totalRequired: number;
  alignmentPercentage: number;
  matchedSkills: string[];
  missingSkills: string[];
}

export interface RoleSkillAlignmentResult {
  roles: RoleSkillAlignmentView[];
}

// API functions

export async function getBusinessRoles(query: BusinessRoleListQuery): Promise<Result<BusinessRoleListView>> {
  const params = new URLSearchParams();
  if (query.search) params.append("search", query.search);
  if (query.status !== undefined) params.append("status", String(query.status));
  if (query.type !== undefined) params.append("type", String(query.type));
  if (query.category) params.append("category", query.category);
  if (query.filterTab) params.append("filterTab", query.filterTab);
  if (query.sortBy) params.append("sortBy", query.sortBy);
  if (query.sortDescending !== undefined) params.append("sortDescending", String(query.sortDescending));
  if (query.page !== undefined) params.append("page", String(query.page));
  if (query.pageSize !== undefined) params.append("pageSize", String(query.pageSize));

  const response = await request.get<Result<BusinessRoleListView>>(`BusinessRole/GetBusinessRoles?${params.toString()}`);
  return response.data;
}

export async function getBusinessRole(id: number): Promise<Result<BusinessRoleView>> {
  const response = await request.get<Result<BusinessRoleView>>(`BusinessRole/GetBusinessRole/${id}`);
  return response.data;
}

export async function getBusinessRoleStats(): Promise<Result<BusinessRoleStatsView>> {
  const response = await request.get<Result<BusinessRoleStatsView>>("BusinessRole/GetBusinessRoleStats");
  return response.data;
}

export async function createBusinessRole(input: CreateBusinessRoleInput): Promise<Result<BusinessRoleView>> {
  const response = await request.post<Result<BusinessRoleView>>("BusinessRole/CreateBusinessRole", input);
  return response.data;
}

export async function updateBusinessRole(input: UpdateBusinessRoleInput): Promise<Result<BusinessRoleView>> {
  const response = await request.put<Result<BusinessRoleView>>("BusinessRole/UpdateBusinessRole", input);
  return response.data;
}

export async function archiveBusinessRole(id: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>(`BusinessRole/ArchiveBusinessRole/${id}`);
  return response.data;
}

export async function getRevisionHistory(roleId: number): Promise<Result<BusinessRoleRevisionView[]>> {
  const response = await request.get<Result<BusinessRoleRevisionView[]>>(`BusinessRole/GetRevisionHistory/${roleId}`);
  return response.data;
}

// Role Assignment API functions (Story 4.2)

export async function detectConflicts(businessRoleId: number, employeeId: string): Promise<Result<ConflictDetectionResult>> {
  const response = await request.get<Result<ConflictDetectionResult>>(
    `BusinessRole/DetectConflicts?businessRoleId=${businessRoleId}&employeeId=${encodeURIComponent(employeeId)}`
  );
  return response.data;
}

export async function assignRole(input: AssignRoleInput): Promise<Result<RoleAssignmentView>> {
  const response = await request.post<Result<RoleAssignmentView>>("BusinessRole/AssignRole", input);
  return response.data;
}

export async function unassignRole(input: UnassignRoleInput): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>("BusinessRole/UnassignRole", input);
  return response.data;
}

export async function getRoleAssignments(roleId: number): Promise<Result<RoleAssignmentListView>> {
  const response = await request.get<Result<RoleAssignmentListView>>(`BusinessRole/GetRoleAssignments/${roleId}`);
  return response.data;
}

export async function getEmployeeRoleAssignments(employeeId: string): Promise<Result<RoleAssignmentListView>> {
  const response = await request.get<Result<RoleAssignmentListView>>(
    `BusinessRole/GetEmployeeRoleAssignments/${encodeURIComponent(employeeId)}`
  );
  return response.data;
}

export async function searchEmployeesForAssignment(search: string): Promise<Result<EmployeeSimpleView[]>> {
  const response = await request.get<Result<EmployeeSimpleView[]>>(
    `BusinessRole/SearchEmployeesForAssignment?search=${encodeURIComponent(search)}`
  );
  return response.data;
}

// Bulk Assignment & Revision History API functions (Story 4.3)

export async function bulkAssignRole(input: BulkAssignRoleInput): Promise<Result<BulkAssignmentResult>> {
  const response = await request.post<Result<BulkAssignmentResult>>("BusinessRole/BulkAssignRole", input);
  return response.data;
}

export async function getUnifiedRevisionHistory(roleId: number): Promise<Result<UnifiedRevisionView[]>> {
  const response = await request.get<Result<UnifiedRevisionView[]>>(`BusinessRole/GetUnifiedRevisionHistory/${roleId}`);
  return response.data;
}

// Role-Skill Alignment API function (Task 12)

export async function getRoleSkillAlignment(employeeId: string): Promise<Result<RoleSkillAlignmentResult>> {
  const response = await request.get<Result<RoleSkillAlignmentResult>>(
    `BusinessRole/GetRoleSkillAlignment/${encodeURIComponent(employeeId)}`
  );
  return response.data;
}
